"use client";

import { memo, useMemo, useState } from "react";
import dynamic from "next/dynamic";
import { ChatResponse, ComparisonItem, PlayerCard, PlayerStat, StatLookupItem, StatsByCategory } from "@/lib/types";

const Plot = dynamic(() => import("react-plotly.js"), { ssr: false });

function formatValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return String(value);
}

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.replace(/,/g, "").trim();
    if (!normalized) {
      return null;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function marketValueToNumber(value?: string | null): number {
  if (!value) {
    return -1;
  }
  const cleaned = value.replace("€", "").trim();
  const multiplier = cleaned.endsWith("M") ? 1_000_000 : cleaned.endsWith("K") ? 1_000 : 1;
  const numeric = toNumber(cleaned.replace(/[MK]/g, ""));
  return numeric === null ? -1 : numeric * multiplier;
}

function formatStatHeaderLabel(statKey: string): string {
  const rawStatName = statKey.includes(":") ? statKey.split(":")[1] : statKey;
  const normalized = rawStatName.replace(/[_-]+/g, " ").trim().toLowerCase();
  if (!normalized) {
    return "Stat";
  }
  return normalized.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

type FlatStat = {
  valueDisplay: string;
  per90Display: string;
  percentileDisplay: string;
  percentilePer90Display: string;
  peerComparisonDisplay: string;
  statRankDisplay: string;
  valueSort: number;
  per90Sort: number;
  percentileSort: number;
  percentilePer90Sort: number;
  peerComparisonSort: number;
  statRankSort: number;
};

type ComparablePlayer = {
  player_id: number;
  name: string;
  position?: string;
  team?: string;
  nationality?: string;
  age?: number;
  market_value?: string | null;
  season?: string;
  appearances?: number;
  minutes_played?: number;
  goals?: number;
  assists?: number;
  rating?: number;
  rating_avg?: number;
  stats_by_category?: StatsByCategory;
};

type PositionGroup =
  | "center_back"
  | "fullback_wingback"
  | "defensive_midfielder"
  | "central_midfielder"
  | "attacking_midfielder"
  | "winger"
  | "striker"
  | "generic";

const CORE_STATS_BY_POSITION_GROUP: Record<PositionGroup, string[]> = {
  center_back: [
    "clearances",
    "interceptions",
    "blocked scoring attempt",
    "tackles",
    "recoveries",
    "dribbled past",
    "duels won",
    "duels won %",
    "aerials won",
    "aerials won %",
    "pass accuracy",
    "accurate passes",
    "accurate long balls",
    "long ball accuracy",
  ],
  fullback_wingback: [
    "tackles",
    "interceptions",
    "blocked scoring attempt",
    "recoveries",
    "clearances",
    "successful crosses",
    "cross accuracy",
    "chances created",
    "big chances created",
    "xA",
    "pass accuracy",
    "accurate passes",
    "touches in opposition box",
    "dribbles",
  ],
  defensive_midfielder: [
    "interceptions",
    "clearances",
    "blocked scoring attempt",
    "tackles",
    "recoveries",
    "duels won",
    "duels won %",
    "aerials won",
    "aerials won %",
    "possession won final 3rd",
    "pass accuracy",
    "accurate passes",
    "accurate long balls",
    "long ball accuracy",
    "chances created",
    "xA",
  ],
  central_midfielder: [
    "pass accuracy",
    "accurate passes",
    "accurate long balls",
    "long ball accuracy",
    "chances created",
    "xA",
    "interceptions",
    "tackles",
    "recoveries",
    "clearances",
    "blocked scoring attempt",
    "duels won",
    "duels won %",
    "aerials won",
    "aerials won %",
    "dribbles",
    "dribbles success rate",
  ],
  attacking_midfielder: [
    "chances created",
    "big chances created",
    "xA",
    "assists",
    "dribbles",
    "dribbles success rate",
    "touches in opposition box",
    "pass accuracy",
    "accurate passes",
    "shots",
    "shots on target",
    "xG",
    "xGOT",
    "goals",
  ],
  winger: [
    "dribbles",
    "dribbles success rate",
    "touches in opposition box",
    "chances created",
    "big chances created",
    "successful crosses",
    "cross accuracy",
    "xA",
    "assists",
    "shots",
    "shots on target",
    "xG",
    "xGOT",
    "goals",
    "recoveries",
  ],
  striker: [
    "shots",
    "shots on target",
    "goals",
    "xG",
    "xGOT",
    "xG excl. penalty",
    "touches in opposition box",
    "dribbles",
    "dribbles success rate",
    "duels won",
    "duels won %",
    "aerials won",
    "aerials won %",
    "chances created",
    "assists",
    "xA",
  ],
  generic: [
    "pass accuracy",
    "accurate passes",
    "tackles",
    "interceptions",
    "recoveries",
    "shots",
    "shots on target",
    "goals",
    "assists",
    "xG",
    "xA",
  ],
};

function normalizeStatName(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function getStatNameFromKey(statKey: string): string {
  return statKey.includes(":") ? statKey.split(":")[1] : statKey;
}

function getRequestedMetricPatterns(metric?: string | null): string[] {
  const normalized = normalizeStatName(metric ?? "");
  if (!normalized) {
    return [];
  }

  if (normalized === "chance_creation") {
    return ["chances created", "big chances created", "key passes", "xa", "x a"];
  }
  if (normalized === "ball_winning") {
    return ["tackles", "interceptions", "recoveries", "duels won"];
  }
  if (normalized === "pass_accuracy") {
    return ["pass accuracy", "accurate passes", "long ball accuracy", "accurate long balls"];
  }

  return [normalized.replace(/_/g, " ")];
}

function inferPositionGroup(position?: string): PositionGroup {
  const normalized = (position ?? "").toLowerCase();

  if (
    normalized.includes("defensive midfielder") ||
    normalized.includes("holding midfielder") ||
    normalized.includes("dm") ||
    normalized.includes("pivot")
  ) {
    return "defensive_midfielder";
  }
  if (
    normalized.includes("attacking midfielder") ||
    normalized.includes("am") ||
    normalized.includes("number 10")
  ) {
    return "attacking_midfielder";
  }
  if (
    normalized.includes("central midfielder") ||
    normalized.includes("centre midfielder") ||
    normalized.includes("cm")
  ) {
    return "central_midfielder";
  }
  if (normalized.includes("midfielder")) {
    return "central_midfielder";
  }
  if (normalized.includes("center back") || normalized.includes("centre-back") || normalized === "defender") {
    return "center_back";
  }
  if (
    normalized.includes("left back") ||
    normalized.includes("right back") ||
    normalized.includes("wing-back") ||
    normalized.includes("wing back") ||
    normalized.includes("full-back") ||
    normalized.includes("full back")
  ) {
    return "fullback_wingback";
  }
  if (normalized.includes("winger") || normalized.includes("left winger") || normalized.includes("right winger")) {
    return "winger";
  }
  if (normalized.includes("striker") || normalized.includes("forward")) {
    return "striker";
  }

  return "generic";
}

function flattenStats(statsByCategory?: StatsByCategory): Record<string, FlatStat> {
  if (!statsByCategory) {
    return {};
  }

  const flattened: Record<string, FlatStat> = {};
  for (const [category, stats] of Object.entries(statsByCategory)) {
    for (const stat of stats) {
      const statKey = `${category}:${stat.name}`;
      const percentile = toNumber(stat.percentile);
      const per90 = toNumber(stat.per_90);
      const rawValue = toNumber(stat.value);

      flattened[statKey] = {
        valueDisplay: formatValue(stat.value),
        per90Display: formatValue(stat.per_90),
        percentileDisplay: formatValue(stat.percentile),
        percentilePer90Display: formatValue(stat.percentile_per_90),
        peerComparisonDisplay: stat.peer_comparison != null ? String(Math.round(stat.peer_comparison)) : "-",
        statRankDisplay: stat.stat_rank != null ? String(Math.round(stat.stat_rank)) : "-",
        valueSort: rawValue ?? Number.NEGATIVE_INFINITY,
        per90Sort: per90 ?? Number.NEGATIVE_INFINITY,
        percentileSort: percentile ?? Number.NEGATIVE_INFINITY,
        percentilePer90Sort: toNumber(stat.percentile_per_90) ?? Number.NEGATIVE_INFINITY,
        peerComparisonSort: stat.peer_comparison != null ? stat.peer_comparison : Number.NEGATIVE_INFINITY,
        statRankSort: stat.stat_rank != null ? stat.stat_rank : Number.NEGATIVE_INFINITY,
      };
    }
  }

  return flattened;
}

function StatTable({ stats }: { stats: PlayerStat[] }) {
  if (!stats || stats.length === 0) {
    return <p className="text-sm text-slate-500">No stats in this category.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-slate-500">
            <th className="py-1 pr-3">Stat</th>
            <th className="py-1 pr-3">Value</th>
            <th className="py-1 pr-3">Per 90</th>
            <th className="py-1 pr-3">Pct</th>
            <th className="py-1">Pct/90</th>
          </tr>
        </thead>
        <tbody>
          {stats.map((stat) => (
            <tr key={`${stat.name}-${stat.value}-${stat.per_90}`} className="border-t border-slate-100">
              <td className="py-1 pr-3">{stat.name}</td>
              <td className="py-1 pr-3">{formatValue(stat.value)}</td>
              <td className="py-1 pr-3">{formatValue(stat.per_90)}</td>
              <td className="py-1 pr-3">{formatValue(stat.percentile)}</td>
              <td className="py-1">{formatValue(stat.percentile_per_90)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CategoryBlocks({ statsByCategory }: { statsByCategory?: StatsByCategory }) {
  if (!statsByCategory || Object.keys(statsByCategory).length === 0) {
    return <p className="text-sm text-slate-500">No detailed stats available.</p>;
  }

  return (
    <div className="space-y-3">
      {Object.entries(statsByCategory).map(([category, stats]) => (
        <details key={category} className="rounded-lg border border-slate-200 bg-white p-3" open>
          <summary className="cursor-pointer text-sm font-semibold capitalize text-slate-800">
            {category} ({stats.length})
          </summary>
          <div className="mt-2">
            <StatTable stats={stats} />
          </div>
        </details>
      ))}
    </div>
  );
}

type SortDirection = "asc" | "desc";

function SortablePlayersTable({
  players,
  title,
  requestedStatMetric,
}: {
  players: ComparablePlayer[];
  title: string;
  requestedStatMetric?: string | null;
}) {
  const [sortKey, setSortKey] = useState<string>("market_value");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const uniquePlayers = useMemo(() => {
    const seen = new Set<number>();
    return players.filter((player) => {
      if (seen.has(player.player_id)) {
        return false;
      }
      seen.add(player.player_id);
      return true;
    });
  }, [players]);

  const rows = useMemo(
    () =>
      uniquePlayers.map((player) => ({
        player,
        flatStats: flattenStats(player.stats_by_category),
      })),
    [uniquePlayers],
  );

  const statColumns = useMemo(() => {
    const columns = new Set<string>();
    for (const row of rows) {
      Object.keys(row.flatStats).forEach((key) => columns.add(key));
    }

    const allColumns = Array.from(columns).sort((left, right) => left.localeCompare(right));

    const requestedPatterns = getRequestedMetricPatterns(requestedStatMetric);
    if (requestedPatterns.length > 0) {
      const metricColumns = allColumns.filter((columnKey) => {
        const normalizedStat = normalizeStatName(getStatNameFromKey(columnKey));
        return requestedPatterns.some((pattern) => normalizedStat.includes(pattern));
      });

      if (metricColumns.length > 0) {
        return metricColumns;
      }
    }

    const groups = new Set<PositionGroup>(rows.map((row) => inferPositionGroup(row.player.position)));

    const allowedStats = new Set<string>();
    for (const group of groups) {
      for (const statName of CORE_STATS_BY_POSITION_GROUP[group]) {
        allowedStats.add(normalizeStatName(statName));
      }
    }

    const filteredColumns = allColumns.filter((columnKey) =>
      allowedStats.has(normalizeStatName(getStatNameFromKey(columnKey))),
    );

    return filteredColumns.length >= 6 ? filteredColumns : allColumns;
  }, [requestedStatMetric, rows]);

  const sortedRows = useMemo(() => {
    const getValue = (row: (typeof rows)[number]): number | string => {
      switch (sortKey) {
        case "name":
          return row.player.name ?? "";
        case "position":
          return row.player.position ?? "";
        case "team":
          return row.player.team ?? "";
        case "age":
          return row.player.age ?? Number.NEGATIVE_INFINITY;
        case "market_value":
          return marketValueToNumber(row.player.market_value);
        case "season":
          return row.player.season ?? "";
        case "appearances":
          return row.player.appearances ?? Number.NEGATIVE_INFINITY;
        case "minutes_played":
          return row.player.minutes_played ?? Number.NEGATIVE_INFINITY;
        case "goals":
          return row.player.goals ?? Number.NEGATIVE_INFINITY;
        case "assists":
          return row.player.assists ?? Number.NEGATIVE_INFINITY;
        case "rating":
          return row.player.rating ?? row.player.rating_avg ?? Number.NEGATIVE_INFINITY;
        default:
          if (sortKey.endsWith("__value")) {
            const key = sortKey.slice(0, -7);
            return row.flatStats[key]?.valueSort ?? Number.NEGATIVE_INFINITY;
          }
          if (sortKey.endsWith("__per90")) {
            const key = sortKey.slice(0, -7);
            return row.flatStats[key]?.per90Sort ?? Number.NEGATIVE_INFINITY;
          }
          if (sortKey.endsWith("__percentile")) {
            const key = sortKey.slice(0, -12);
            return row.flatStats[key]?.percentileSort ?? Number.NEGATIVE_INFINITY;
          }
          if (sortKey.endsWith("__percentile_per90")) {
            const key = sortKey.slice(0, -17);
            return row.flatStats[key]?.percentilePer90Sort ?? Number.NEGATIVE_INFINITY;
          }
          if (sortKey.endsWith("__peer_comparison")) {
            const key = sortKey.slice(0, -17);
            return row.flatStats[key]?.peerComparisonSort ?? Number.NEGATIVE_INFINITY;
          }
          if (sortKey.endsWith("__stat_rank")) {
            const key = sortKey.slice(0, -11);
            return row.flatStats[key]?.statRankSort ?? Number.NEGATIVE_INFINITY;
          }
          return Number.NEGATIVE_INFINITY;
      }
    };

    return [...rows].sort((left, right) => {
      const leftValue = getValue(left);
      const rightValue = getValue(right);

      if (typeof leftValue === "string" && typeof rightValue === "string") {
        const comparison = leftValue.localeCompare(rightValue);
        return sortDirection === "asc" ? comparison : -comparison;
      }

      const comparison = Number(leftValue) - Number(rightValue);
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [rows, sortDirection, sortKey]);

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection("desc");
  };

  if (!players.length) {
    return <p className="text-sm text-slate-500">No players returned.</p>;
  }

  return (
    <div className="max-w-full space-y-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-sm font-semibold text-slate-800">{title}</p>
      <p className="text-xs text-slate-500">Click any column header to sort. Default: market value descending.</p>
      <div className="max-w-full overflow-x-auto">
        <table className="min-w-max text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-slate-600">
              {[
                ["name", "Player"],
                ["position", "Position"],
                ["team", "Team"],
                ["age", "Age"],
                ["market_value", "Market Value"],
                ["season", "Season"],
                ["appearances", "Apps"],
                ["minutes_played", "Minutes"],
                ["goals", "Goals"],
                ["assists", "Assists"],
                ["rating", "Rating"],
              ].map(([key, label]) => (
                <th key={key} className="px-3 py-2 whitespace-nowrap">
                  <button className="cursor-pointer" onClick={() => toggleSort(key)}>
                    {label} {sortKey === key ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
                  </button>
                </th>
              ))}
              {statColumns.flatMap((statKey) => {
                const label = formatStatHeaderLabel(statKey);
                // Determine which sub-columns to show: if any player has peer_comparison
                // for this stat, show Value | Peer% | Rank; otherwise show Value | Per90 | Pct | Pct/90.
                const hasPeerData = rows.some((r) => r.flatStats[statKey]?.peerComparisonSort !== Number.NEGATIVE_INFINITY);
                const subCols: [string, string][] = hasPeerData
                  ? [["__value", "Value"], ["__peer_comparison", "Peer%"], ["__stat_rank", "Rank"]]
                  : [["__value", "Value"], ["__per90", "Per90"], ["__percentile", "Pct"], ["__percentile_per90", "Pct/90"]];
                return subCols.map(([suffix, suffixLabel]) => {
                  const composedKey = `${statKey}${suffix}`;
                  return (
                    <th key={composedKey} className="px-3 py-2 whitespace-nowrap">
                      <button className="cursor-pointer" onClick={() => toggleSort(composedKey)}>
                        {label} {suffixLabel} {sortKey === composedKey ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
                      </button>
                    </th>
                  );
                });
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map(({ player, flatStats }) => (
              <tr key={player.player_id} className="border-b border-slate-100 align-top">
                <td className="px-3 py-2 font-medium text-slate-900 whitespace-nowrap">{player.name}</td>
                <td className="px-3 py-2 whitespace-nowrap">{player.position ?? "-"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{player.team ?? "-"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{player.age ?? "-"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{player.market_value ?? "-"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{player.season ?? "-"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{player.appearances ?? "-"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{player.minutes_played ?? "-"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{player.goals ?? "-"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{player.assists ?? "-"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{player.rating ?? player.rating_avg ?? "-"}</td>
                {statColumns.flatMap((statKey) => {
                  const stat = flatStats[statKey];
                  const hasPeerData = rows.some((r) => r.flatStats[statKey]?.peerComparisonSort !== Number.NEGATIVE_INFINITY);
                  if (hasPeerData) {
                    return [
                      <td key={`${player.player_id}-${statKey}-value`} className="px-3 py-2 whitespace-nowrap text-slate-700">{stat?.valueDisplay ?? "-"}</td>,
                      <td key={`${player.player_id}-${statKey}-peer`} className="px-3 py-2 whitespace-nowrap text-slate-700">{stat?.peerComparisonDisplay ?? "-"}</td>,
                      <td key={`${player.player_id}-${statKey}-rank`} className="px-3 py-2 whitespace-nowrap text-slate-700">{stat?.statRankDisplay ?? "-"}</td>,
                    ];
                  }
                  return [
                    <td key={`${player.player_id}-${statKey}-value`} className="px-3 py-2 whitespace-nowrap text-slate-700">{stat?.valueDisplay ?? "-"}</td>,
                    <td key={`${player.player_id}-${statKey}-per90`} className="px-3 py-2 whitespace-nowrap text-slate-700">{stat?.per90Display ?? "-"}</td>,
                    <td key={`${player.player_id}-${statKey}-pct`} className="px-3 py-2 whitespace-nowrap text-slate-700">{stat?.percentileDisplay ?? "-"}</td>,
                    <td key={`${player.player_id}-${statKey}-pct90`} className="px-3 py-2 whitespace-nowrap text-slate-700">{stat?.percentilePer90Display ?? "-"}</td>,
                  ];
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PlayerListBlock({ players, requestedStatMetric }: { players: PlayerCard[]; requestedStatMetric?: string | null }) {
  if (!players.length) {
    return <p className="text-sm text-slate-500">No players returned.</p>;
  }

  if (players.length > 1) {
    return (
      <SortablePlayersTable
        players={players}
        title={`Comparing ${players.length} players side by side`}
        requestedStatMetric={requestedStatMetric}
      />
    );
  }

  return (
    <div className="space-y-4">
      {players.map((player) => (
        <article key={player.player_id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
            <h3 className="text-base font-semibold text-slate-900">{player.name}</h3>
            <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{player.position}</span>
            <span className="text-sm text-slate-600">{player.team}</span>
          </div>
          <p className="mb-3 text-sm text-slate-600">
            {player.nationality} • Age {player.age ?? "-"} • {player.market_value ?? "-"} • Season {player.season ?? "-"} • Apps {player.appearances ?? "-"} • Minutes {player.minutes_played ?? "-"}
          </p>
          <CategoryBlocks statsByCategory={player.stats_by_category} />
        </article>
      ))}
    </div>
  );
}

function StatLookupBlock({ item }: { item: StatLookupItem }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="text-base font-semibold text-slate-900">{item.player.name}</h3>
      <p className="mb-3 text-sm text-slate-600">
        {item.player.position ?? "-"} • {item.player.team ?? "-"} • {item.player.nationality ?? "-"} • {item.player.market_value ?? "-"}
      </p>
      <CategoryBlocks statsByCategory={item.stats_by_category} />
    </div>
  );
}

function ComparisonBlock({ item, requestedStatMetric }: { item: ComparisonItem; requestedStatMetric?: string | null }) {
  if (item.players.length > 1) {
    return (
      <SortablePlayersTable
        players={item.players}
        title={`Comparison table (${item.comparison_count} players)`}
        requestedStatMetric={requestedStatMetric}
      />
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">Comparing {item.comparison_count} players</p>
      {item.players.map((player) => (
        <article key={player.player_id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900">{player.name}</h3>
          <p className="mb-3 text-sm text-slate-600">{player.position ?? "-"} • {player.team ?? "-"}</p>
          <CategoryBlocks statsByCategory={player.stats_by_category} />
        </article>
      ))}
    </div>
  );
}

function ChartBlock({ response }: { response: ChatResponse }) {
  if (!response.chart) {
    return null;
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold text-slate-800">{response.chart.title || "Chart"}</h3>
      <Plot
        data={response.chart.data as never[]}
        layout={{
          autosize: true,
          ...(response.chart.layout ?? {}),
          margin: { l: 30, r: 20, t: 30, b: 30 },
          paper_bgcolor: "#ffffff",
          plot_bgcolor: "#ffffff",
        }}
        style={{ width: "100%", height: "360px" }}
        useResizeHandler
        config={{ displayModeBar: false, responsive: true }}
      />
    </div>
  );
}

export const ResponseRenderer = memo(function ResponseRenderer({ response }: { response: ChatResponse }) {
  const intent = response.intent;
  const data = (response.data ?? []) as unknown[];
  const requestedStatMetric = response.metadata.requested_stat_metric;
  const comparisonFocus = response.metadata.comparison_focus;

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
        <p className="text-sm font-medium text-slate-900">{response.message}</p>
        <p className="mt-1 text-xs text-slate-500">
          Intent: {intent ?? "unknown"} • Tool: {response.metadata.tool_used ?? "-"} • {response.metadata.execution_time_ms}ms
        </p>
      </div>

      {intent === "player_search" || intent === "recruitment" ? (
        <PlayerListBlock players={data as unknown as PlayerCard[]} requestedStatMetric={requestedStatMetric} />
      ) : null}

      {intent === "stat_lookup" && data.length > 0 ? (
        <StatLookupBlock item={data[0] as unknown as StatLookupItem} />
      ) : null}

      {intent === "comparison" && data.length > 0 ? (
        <ComparisonBlock
          item={data[0] as unknown as ComparisonItem}
          requestedStatMetric={comparisonFocus ?? requestedStatMetric}
        />
      ) : null}

      <ChartBlock response={response} />
    </div>
  );
});
